'use client';
import React, { useState, useEffect, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import Icon from '@/components/ui/AppIcon';
import Breadcrumb from '@/components/Breadcrumb';
import { toast, Toaster } from 'sonner';

const supabase = createClient();

interface CentroCosto {
  id: string;
  codice: string;
  nome: string;
  reparto: string;
  budget_annuale: number;
  attivo: boolean;
  created_at: string;
}

interface CostoManutenzione {
  id: string;
  centro_costo_id: string | null;
  tipo_costo: string;
  descrizione: string;
  importo: number;
  data_costo: string;
  approvato: boolean;
  created_at: string;
}

const TIPO_COSTO = ['manodopera', 'ricambio', 'contratto', 'licenza', 'altro'];

const tipoColor: Record<string, string> = {
  manodopera: 'bg-blue-500/20 text-blue-400',
  ricambio: 'bg-orange-500/20 text-orange-400',
  contratto: 'bg-purple-500/20 text-purple-400',
  licenza: 'bg-green-500/20 text-green-400',
  altro: 'bg-gray-500/20 text-gray-400',
};

export default function CentriCostoContent() {
  const [centri, setCentri] = useState<CentroCosto[]>([]);
  const [costi, setCosti] = useState<CostoManutenzione[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'centri' | 'costi' | 'analisi'>('centri');
  const [showCentroForm, setShowCentroForm] = useState(false);
  const [showCostoForm, setShowCostoForm] = useState(false);
  const [centrForm, setCentroForm] = useState({ codice: '', nome: '', reparto: '', budget_annuale: 0 });
  const [costoForm, setCostoForm] = useState({ centro_costo_id: '', tipo_costo: 'manodopera', descrizione: '', importo: 0, data_costo: new Date().toISOString().split('T')[0] });
  const [saving, setSaving] = useState(false);
  const [selectedCentro, setSelectedCentro] = useState<string>('');
  const { profile } = useAuth();

  const [editCentro, setEditCentro] = useState<CentroCosto | null>(null);
  const [editCosto, setEditCosto] = useState<CostoManutenzione | null>(null);
  const [showEditCentroForm, setShowEditCentroForm] = useState(false);
  const [showEditCostoForm, setShowEditCostoForm] = useState(false);
  const [editCentroForm, setEditCentroForm] = useState({ codice: '', nome: '', reparto: '', budget_annuale: 0 });
  const [editCostoForm, setEditCostoForm] = useState({ centro_costo_id: '', tipo_costo: 'manodopera', descrizione: '', importo: 0, data_costo: '' });
  const [deleteConfirm, setDeleteConfirm] = useState<{ open: boolean; id: string; name: string; table: string }>({ open: false, id: '', name: '', table: '' });

  const fetchData = useCallback(async () => {
    setLoading(true);
    const [{ data: c }, { data: co }] = await Promise.all([
      supabase.from('centri_di_costo').select('*').order('codice'),
      supabase.from('costi_manutenzione').select('*').order('data_costo', { ascending: false }),
    ]);
    setCentri(c || []);
    setCosti(co || []);
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const saveCentro = async () => {
    if (!centrForm.codice || !centrForm.nome) { toast.error('Codice e nome obbligatori'); return; }
    if (centrForm.budget_annuale < 0) { toast.error('Budget non può essere negativo'); return; }
    setSaving(true);
    const { error } = await supabase.from('centri_di_costo').insert({ ...centrForm, responsabile_id: profile?.id });
    if (error) { toast.error('Errore: ' + error.message); } else { toast.success('Centro di costo creato'); }
    setShowCentroForm(false);
    setCentroForm({ codice: '', nome: '', reparto: '', budget_annuale: 0 });
    setSaving(false);
    fetchData();
  };

  const saveCosto = async () => {
    if (!costoForm.descrizione) { toast.error('Descrizione obbligatoria'); return; }
    if (costoForm.importo <= 0) { toast.error('Importo deve essere maggiore di 0'); return; }
    setSaving(true);
    const { error } = await supabase.from('costi_manutenzione').insert({
      ...costoForm,
      centro_costo_id: costoForm.centro_costo_id || null,
    });
    if (error) { toast.error('Errore: ' + error.message); } else { toast.success('Costo aggiunto'); }
    setShowCostoForm(false);
    setCostoForm({ centro_costo_id: '', tipo_costo: 'manodopera', descrizione: '', importo: 0, data_costo: new Date().toISOString().split('T')[0] });
    setSaving(false);
    fetchData();
  };

  const approvaCosto = async (id: string, approvato: boolean) => {
    await supabase.from('costi_manutenzione').update({ approvato: !approvato, approvato_da: profile?.id }).eq('id', id);
    setCosti(prev => prev.map(c => c.id === id ? { ...c, approvato: !approvato } : c));
  };

  const costiFiltered = selectedCentro ? costi.filter(c => c.centro_costo_id === selectedCentro) : costi;
  const totaleCosti = costiFiltered.reduce((sum, c) => sum + (c.importo || 0), 0);
  const costiPerTipo = TIPO_COSTO.map(tipo => ({
    tipo,
    totale: costiFiltered.filter(c => c.tipo_costo === tipo).reduce((sum, c) => sum + (c.importo || 0), 0),
    count: costiFiltered.filter(c => c.tipo_costo === tipo).length,
  })).filter(t => t.totale > 0);

  const costiPerCentro = centri.map(centro => ({
    ...centro,
    totale_costi: costi.filter(c => c.centro_costo_id === centro.id).reduce((sum, c) => sum + (c.importo || 0), 0),
    num_voci: costi.filter(c => c.centro_costo_id === centro.id).length,
    budget_usato_pct: centro.budget_annuale > 0
      ? Math.min(100, (costi.filter(c => c.centro_costo_id === centro.id).reduce((sum, c) => sum + (c.importo || 0), 0) / centro.budget_annuale) * 100)
      : 0,
  }));

  const openEditCentro = (c: CentroCosto) => {
    setEditCentro(c);
    setEditCentroForm({ codice: c.codice, nome: c.nome, reparto: c.reparto || '', budget_annuale: c.budget_annuale });
    setShowEditCentroForm(true);
  };

  const openEditCosto = (c: CostoManutenzione) => {
    setEditCosto(c);
    setEditCostoForm({ centro_costo_id: c.centro_costo_id || '', tipo_costo: c.tipo_costo, descrizione: c.descrizione, importo: c.importo, data_costo: c.data_costo });
    setShowEditCostoForm(true);
  };

  const handleEditCentro = async () => {
    if (!editCentro) return;
    if (!editCentroForm.codice || !editCentroForm.nome) { toast.error('Codice e nome obbligatori'); return; }
    if (editCentroForm.budget_annuale < 0) { toast.error('Budget non può essere negativo'); return; }
    setSaving(true);
    const { error } = await supabase.from('centri_di_costo').update(editCentroForm).eq('id', editCentro.id);
    if (error) { toast.error('Errore: ' + error.message); } else { toast.success('Centro aggiornato'); }
    setShowEditCentroForm(false);
    setSaving(false);
    fetchData();
  };

  const handleEditCosto = async () => {
    if (!editCosto) return;
    if (!editCostoForm.descrizione) { toast.error('Descrizione obbligatoria'); return; }
    if (editCostoForm.importo <= 0) { toast.error('Importo deve essere > 0'); return; }
    setSaving(true);
    const { error } = await supabase.from('costi_manutenzione').update({ ...editCostoForm, centro_costo_id: editCostoForm.centro_costo_id || null }).eq('id', editCosto.id);
    if (error) { toast.error('Errore: ' + error.message); } else { toast.success('Costo aggiornato'); }
    setShowEditCostoForm(false);
    setSaving(false);
    fetchData();
  };

  const handleDelete = async () => {
    const { error } = await supabase.from(deleteConfirm.table).delete().eq('id', deleteConfirm.id);
    if (error) { toast.error('Errore eliminazione: ' + error.message); } else { toast.success('Eliminato'); }
    setDeleteConfirm({ open: false, id: '', name: '', table: '' });
    fetchData();
  };

  return (
    <div className="space-y-6">
      <Toaster position="bottom-right" theme="dark" richColors />
      <Breadcrumb items={[{ label: 'Analisi Costi per Centro di Costo' }]} />

      {/* Delete Confirm */}
      {deleteConfirm.open && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-surface border border-border rounded-2xl w-full max-w-sm shadow-2xl p-6">
            <h3 className="text-lg font-bold text-white mb-2">Elimina</h3>
            <p className="text-secondary-color text-sm mb-5">Sei sicuro di voler eliminare <strong className="text-white">{deleteConfirm.name}</strong>?</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteConfirm({ open: false, id: '', name: '', table: '' })} className="flex-1 py-2.5 rounded-xl border border-border text-sm font-semibold text-secondary-color hover:text-white hover:bg-surface-elevated transition-all">Annulla</button>
              <button onClick={handleDelete} className="flex-1 py-2.5 rounded-xl bg-red-500 hover:bg-red-400 text-white text-sm font-bold transition-all">Elimina</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Centro Modal */}
      {showEditCentroForm && editCentro && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-surface border border-border rounded-2xl w-full max-w-md shadow-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">Modifica Centro di Costo</h3>
              <button onClick={() => setShowEditCentroForm(false)} className="text-muted-color hover:text-white"><Icon name="XMarkIcon" size={20} /></button>
            </div>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-muted-color mb-1">Codice *</label>
                  <input value={editCentroForm.codice} onChange={e => setEditCentroForm(p => ({ ...p, codice: e.target.value }))} className="w-full bg-surface-elevated border border-border rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-sky-500" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-muted-color mb-1">Nome *</label>
                  <input value={editCentroForm.nome} onChange={e => setEditCentroForm(p => ({ ...p, nome: e.target.value }))} className="w-full bg-surface-elevated border border-border rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-sky-500" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-muted-color mb-1">Reparto</label>
                <input value={editCentroForm.reparto} onChange={e => setEditCentroForm(p => ({ ...p, reparto: e.target.value }))} className="w-full bg-surface-elevated border border-border rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-sky-500" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-muted-color mb-1">Budget Annuale (€)</label>
                <input type="number" min="0" step="0.01" value={editCentroForm.budget_annuale} onChange={e => setEditCentroForm(p => ({ ...p, budget_annuale: parseFloat(e.target.value) || 0 }))} className="w-full bg-surface-elevated border border-border rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-sky-500" />
              </div>
            </div>
            <div className="flex gap-3 mt-5">
              <button onClick={() => setShowEditCentroForm(false)} className="flex-1 py-2.5 rounded-xl border border-border text-sm font-semibold text-secondary-color hover:text-white hover:bg-surface-elevated transition-all">Annulla</button>
              <button onClick={handleEditCentro} disabled={saving} className="flex-1 py-2.5 rounded-xl bg-sky-500 hover:bg-sky-400 disabled:opacity-60 text-white text-sm font-bold transition-all">{saving ? 'Salvataggio...' : 'Salva'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Costo Modal */}
      {showEditCostoForm && editCosto && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-surface border border-border rounded-2xl w-full max-w-md shadow-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">Modifica Voce di Costo</h3>
              <button onClick={() => setShowEditCostoForm(false)} className="text-muted-color hover:text-white"><Icon name="XMarkIcon" size={20} /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-muted-color mb-1">Descrizione *</label>
                <input value={editCostoForm.descrizione} onChange={e => setEditCostoForm(p => ({ ...p, descrizione: e.target.value }))} className="w-full bg-surface-elevated border border-border rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-sky-500" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-muted-color mb-1">Tipo</label>
                  <select value={editCostoForm.tipo_costo} onChange={e => setEditCostoForm(p => ({ ...p, tipo_costo: e.target.value }))} className="w-full bg-surface-elevated border border-border rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-sky-500">
                    {TIPO_COSTO.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-muted-color mb-1">Importo (€) *</label>
                  <input type="number" min="0.01" step="0.01" value={editCostoForm.importo} onChange={e => setEditCostoForm(p => ({ ...p, importo: parseFloat(e.target.value) || 0 }))} className="w-full bg-surface-elevated border border-border rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-sky-500" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-muted-color mb-1">Data</label>
                <input type="date" value={editCostoForm.data_costo} onChange={e => setEditCostoForm(p => ({ ...p, data_costo: e.target.value }))} className="w-full bg-surface-elevated border border-border rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-sky-500" />
              </div>
            </div>
            <div className="flex gap-3 mt-5">
              <button onClick={() => setShowEditCostoForm(false)} className="flex-1 py-2.5 rounded-xl border border-border text-sm font-semibold text-secondary-color hover:text-white hover:bg-surface-elevated transition-all">Annulla</button>
              <button onClick={handleEditCosto} disabled={saving} className="flex-1 py-2.5 rounded-xl bg-sky-500 hover:bg-sky-400 disabled:opacity-60 text-white text-sm font-bold transition-all">{saving ? 'Salvataggio...' : 'Salva'}</button>
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Analisi Costi per Centro di Costo</h1>
          <p className="text-text-secondary text-sm mt-1">Monitora e analizza i costi di manutenzione per reparto/centro</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setShowCostoForm(true)} className="flex items-center gap-2 bg-surface-elevated hover:bg-surface border border-border text-white px-4 py-2 rounded-lg text-sm font-semibold transition-colors">
            <Icon name="PlusIcon" size={16} /> Aggiungi Costo
          </button>
          <button onClick={() => setShowCentroForm(true)} className="flex items-center gap-2 bg-sky-600 hover:bg-sky-500 text-white px-4 py-2 rounded-lg text-sm font-semibold transition-colors">
            <Icon name="BuildingOffice2Icon" size={16} /> Nuovo Centro
          </button>
        </div>
      </div>

      {/* KPI */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Totale Costi', value: `€${totaleCosti.toLocaleString('it-IT', { minimumFractionDigits: 2 })}`, color: 'text-red-400', icon: 'CurrencyEuroIcon' },
          { label: 'Centri di Costo', value: centri.length, color: 'text-sky-400', icon: 'BuildingOffice2Icon' },
          { label: 'Voci di Costo', value: costi.length, color: 'text-amber-400', icon: 'DocumentTextIcon' },
          { label: 'Da Approvare', value: costi.filter(c => !c.approvato).length, color: 'text-orange-400', icon: 'ClockIcon' },
        ].map(s => (
          <div key={s.label} className="bg-surface-elevated border border-border rounded-xl p-4">
            <div className="flex items-center gap-2 mb-1">
              <Icon name={s.icon as any} size={16} className={s.color} />
              <span className="text-xs text-muted-color">{s.label}</span>
            </div>
            <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-border">
        {(['centri', 'costi', 'analisi'] as const).map(tab => (
          <button key={tab} onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors capitalize ${activeTab === tab ? 'border-sky-500 text-sky-400' : 'border-transparent text-muted-color hover:text-white'}`}>
            {tab === 'centri' ? 'Centri di Costo' : tab === 'costi' ? 'Voci di Costo' : 'Analisi'}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12"><div className="w-8 h-8 border-2 border-sky-500 border-t-transparent rounded-full animate-spin" /></div>
      ) : activeTab === 'centri' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {costiPerCentro.map(centro => (
            <div key={centro.id} className="bg-surface-elevated border border-border rounded-xl p-4">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <p className="font-semibold text-white">{centro.nome}</p>
                  <p className="text-xs text-muted-color">{centro.codice} · {centro.reparto || 'N/A'}</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-xs px-2 py-0.5 rounded-full ${centro.attivo ? 'bg-green-500/10 text-green-400' : 'bg-gray-500/10 text-gray-400'}`}>
                    {centro.attivo ? 'Attivo' : 'Inattivo'}
                  </span>
                  <button onClick={() => openEditCentro(centro)} className="p-1.5 text-muted-color hover:text-sky-400 hover:bg-sky-500/10 rounded-lg transition-colors" title="Modifica">
                    <Icon name="PencilIcon" size={14} />
                  </button>
                  <button onClick={() => setDeleteConfirm({ open: true, id: centro.id, name: centro.nome, table: 'centri_di_costo' })} className="p-1.5 text-muted-color hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors" title="Elimina">
                    <Icon name="TrashIcon" size={14} />
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-color">Costi totali</span>
                  <span className="text-white font-medium">€{centro.totale_costi.toLocaleString('it-IT', { minimumFractionDigits: 2 })}</span>
                </div>
                {centro.budget_annuale > 0 && (
                  <>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-color">Budget annuale</span>
                      <span className="text-white">€{centro.budget_annuale.toLocaleString('it-IT')}</span>
                    </div>
                    <div className="w-full bg-surface rounded-full h-2">
                      <div
                        className={`h-2 rounded-full transition-all ${centro.budget_usato_pct > 90 ? 'bg-red-500' : centro.budget_usato_pct > 70 ? 'bg-amber-500' : 'bg-green-500'}`}
                        style={{ width: `${centro.budget_usato_pct}%` }}
                      />
                    </div>
                    <p className="text-xs text-muted-color">{centro.budget_usato_pct.toFixed(1)}% del budget utilizzato</p>
                  </>
                )}
                <p className="text-xs text-muted-color">{centro.num_voci} voci di costo</p>
              </div>
            </div>
          ))}
          {centri.length === 0 && (
            <div className="col-span-3 text-center py-12 text-muted-color">
              <Icon name="BuildingOffice2Icon" size={48} className="mx-auto mb-3 opacity-30" />
              <p>Nessun centro di costo. Creane uno!</p>
            </div>
          )}
        </div>
      ) : activeTab === 'costi' ? (
        <div className="space-y-3">
          <div className="flex gap-3 flex-wrap">
            <select value={selectedCentro} onChange={e => setSelectedCentro(e.target.value)}
              className="bg-surface-elevated border border-border rounded-lg px-3 py-2 text-sm text-white">
              <option value="">Tutti i centri</option>
              {centri.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
            </select>
          </div>
          {costiFiltered.length === 0 ? (
            <div className="text-center py-12 text-muted-color">Nessuna voce di costo</div>
          ) : costiFiltered.map(costo => (
            <div key={costo.id} className="bg-surface-elevated border border-border rounded-xl p-4 flex items-center gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className={`text-xs px-2 py-0.5 rounded-full ${tipoColor[costo.tipo_costo] || 'bg-gray-500/20 text-gray-400'}`}>{costo.tipo_costo}</span>
                  <span className="text-xs text-muted-color">{new Date(costo.data_costo).toLocaleDateString('it-IT')}</span>
                </div>
                <p className="text-sm text-white">{costo.descrizione}</p>
                {costo.centro_costo_id && (
                  <p className="text-xs text-muted-color mt-0.5">{centri.find(c => c.id === costo.centro_costo_id)?.nome || 'Centro sconosciuto'}</p>
                )}
              </div>
              <div className="text-right flex-shrink-0 flex items-center gap-2">
                <div>
                  <p className="text-lg font-bold text-white">€{(costo.importo || 0).toLocaleString('it-IT', { minimumFractionDigits: 2 })}</p>
                  <button onClick={() => approvaCosto(costo.id, costo.approvato)}
                    className={`text-xs px-2 py-0.5 rounded-full transition-colors ${costo.approvato ? 'bg-green-500/10 text-green-400' : 'bg-amber-500/10 text-amber-400 hover:bg-green-500/10 hover:text-green-400'}`}>
                    {costo.approvato ? '✓ Approvato' : 'Da approvare'}
                  </button>
                </div>
                <button onClick={() => openEditCosto(costo)} className="p-1.5 text-muted-color hover:text-sky-400 hover:bg-sky-500/10 rounded-lg transition-colors" title="Modifica">
                  <Icon name="PencilIcon" size={14} />
                </button>
                <button onClick={() => setDeleteConfirm({ open: true, id: costo.id, name: costo.descrizione, table: 'costi_manutenzione' })} className="p-1.5 text-muted-color hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors" title="Elimina">
                  <Icon name="TrashIcon" size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-surface-elevated border border-border rounded-xl p-4">
            <h3 className="font-semibold text-white mb-4">Costi per Tipo</h3>
            <div className="space-y-3">
              {costiPerTipo.map(t => (
                <div key={t.tipo}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-muted-color capitalize">{t.tipo}</span>
                    <span className="text-white">€{t.totale.toLocaleString('it-IT', { minimumFractionDigits: 2 })} ({t.count} voci)</span>
                  </div>
                  <div className="w-full bg-surface rounded-full h-2">
                    <div className="h-2 rounded-full bg-sky-500" style={{ width: `${totaleCosti > 0 ? (t.totale / totaleCosti) * 100 : 0}%` }} />
                  </div>
                </div>
              ))}
              {costiPerTipo.length === 0 && <p className="text-muted-color text-sm">Nessun dato disponibile</p>}
            </div>
          </div>
          <div className="bg-surface-elevated border border-border rounded-xl p-4">
            <h3 className="font-semibold text-white mb-4">Top Centri per Spesa</h3>
            <div className="space-y-3">
              {costiPerCentro.sort((a, b) => b.totale_costi - a.totale_costi).slice(0, 5).map(c => (
                <div key={c.id}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-muted-color">{c.nome}</span>
                    <span className="text-white">€{c.totale_costi.toLocaleString('it-IT', { minimumFractionDigits: 2 })}</span>
                  </div>
                  <div className="w-full bg-surface rounded-full h-2">
                    <div className="h-2 rounded-full bg-amber-500"
                      style={{ width: `${totaleCosti > 0 ? (c.totale_costi / totaleCosti) * 100 : 0}%` }} />
                  </div>
                </div>
              ))}
              {costiPerCentro.every(c => c.totale_costi === 0) && <p className="text-muted-color text-sm">Nessun costo registrato</p>}
            </div>
          </div>
        </div>
      )}

      {/* Centro Form Modal */}
      {showCentroForm && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-surface border border-border rounded-2xl w-full max-w-md">
            <div className="flex items-center justify-between p-6 border-b border-border">
              <h2 className="text-lg font-bold text-white">Nuovo Centro di Costo</h2>
              <button onClick={() => setShowCentroForm(false)} className="p-2 rounded-lg hover:bg-surface-elevated text-muted-color hover:text-white transition-colors"><Icon name="XMarkIcon" size={20} /></button>
            </div>
            <div className="p-6 space-y-4">
              {[
                { label: 'Codice *', key: 'codice', placeholder: 'Es. CC-IT-001' },
                { label: 'Nome *', key: 'nome', placeholder: 'Es. Reparto IT' },
                { label: 'Reparto', key: 'reparto', placeholder: 'Es. Informatica' },
              ].map(f => (
                <div key={f.key}>
                  <label className="block text-sm text-muted-color mb-1">{f.label}</label>
                  <input value={(centrForm as any)[f.key]} onChange={e => setCentroForm(p => ({ ...p, [f.key]: e.target.value }))}
                    className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2 text-sm text-white" placeholder={f.placeholder} />
                </div>
              ))}
              <div>
                <label className="block text-sm text-muted-color mb-1">Budget Annuale (€)</label>
                <input type="number" value={centrForm.budget_annuale} onChange={e => setCentroForm(p => ({ ...p, budget_annuale: Number(e.target.value) }))}
                  className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2 text-sm text-white" />
              </div>
            </div>
            <div className="flex gap-3 p-6 border-t border-border">
              <button onClick={() => setShowCentroForm(false)} className="flex-1 bg-surface-elevated border border-border text-white px-4 py-2 rounded-lg text-sm font-semibold">Annulla</button>
              <button onClick={saveCentro} disabled={saving} className="flex-1 bg-sky-600 hover:bg-sky-500 disabled:opacity-50 text-white px-4 py-2 rounded-lg text-sm font-semibold">
                {saving ? 'Salvataggio...' : 'Crea Centro'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Costo Form Modal */}
      {showCostoForm && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-surface border border-border rounded-2xl w-full max-w-md">
            <div className="flex items-center justify-between p-6 border-b border-border">
              <h2 className="text-lg font-bold text-white">Aggiungi Voce di Costo</h2>
              <button onClick={() => setShowCostoForm(false)} className="p-2 rounded-lg hover:bg-surface-elevated text-muted-color hover:text-white transition-colors"><Icon name="XMarkIcon" size={20} /></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm text-muted-color mb-1">Centro di Costo</label>
                <select value={costoForm.centro_costo_id} onChange={e => setCostoForm(p => ({ ...p, centro_costo_id: e.target.value }))}
                  className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2 text-sm text-white">
                  <option value="">Nessun centro</option>
                  {centri.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm text-muted-color mb-1">Tipo Costo</label>
                <select value={costoForm.tipo_costo} onChange={e => setCostoForm(p => ({ ...p, tipo_costo: e.target.value }))}
                  className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2 text-sm text-white">
                  {TIPO_COSTO.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm text-muted-color mb-1">Descrizione *</label>
                <input value={costoForm.descrizione} onChange={e => setCostoForm(p => ({ ...p, descrizione: e.target.value }))}
                  className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2 text-sm text-white" placeholder="Es. Sostituzione disco SSD" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm text-muted-color mb-1">Importo (€) *</label>
                  <input type="number" step="0.01" value={costoForm.importo} onChange={e => setCostoForm(p => ({ ...p, importo: Number(e.target.value) }))}
                    className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2 text-sm text-white" />
                </div>
                <div>
                  <label className="block text-sm text-muted-color mb-1">Data</label>
                  <input type="date" value={costoForm.data_costo} onChange={e => setCostoForm(p => ({ ...p, data_costo: e.target.value }))}
                    className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2 text-sm text-white" />
                </div>
              </div>
            </div>
            <div className="flex gap-3 p-6 border-t border-border">
              <button onClick={() => setShowCostoForm(false)} className="flex-1 bg-surface-elevated border border-border text-white px-4 py-2 rounded-lg text-sm font-semibold">Annulla</button>
              <button onClick={saveCosto} disabled={saving} className="flex-1 bg-sky-600 hover:bg-sky-500 disabled:opacity-50 text-white px-4 py-2 rounded-lg text-sm font-semibold">
                {saving ? 'Salvataggio...' : 'Aggiungi Costo'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
